import { takeLatest, put, call, select } from 'redux-saga/effects';
import {
    setLoading,
    setSuccess,
    setAccountList,
    setAccountCreation,
    clearAccountData,
    setDistrictList,
    setLoadingDistrict,
    setLoadingProvince,
    setLoadingWard,
    setProvinceList,
    setWardList,
    setRoleList,
    setLoadingRole,
    setProvinceScList,
    setDistrictScList,
    setWardScList,
    setLoadingScDistrict,
    setLoadingScProvince,
    setLoadingScWard,
    setEnrolledList,
    setUnEnrolledList,
    setLoadingEnrollments,
    setLoadingCreateAccount,
    setFailedCreateAccount,
    setExportFile,
    setExportFileUrl,
    setLoadingExport,
    setLoadingDownloadTemplate,
    setTemplateFile,
    setHistoryLogs,
    setLoadingHistoryLogs,
    setDeleteHistoryLogs,
    setLoadingDeleteHistoryLogs,
    setLoadingDeleteUser,
    setDeleteUserSuccess,
    setImportProgressSession,
    setDeleteProgressSession,
    setValidationSession,
    setDownloadValidatedExcelFile,
    setValidationPreFilterFailures,
} from '@store/reducers/accountReducer';
import { saveImportProgressSession } from '@features/admin/account/AccountOverview/importProgressStorage';
import { saveDeleteProgressSession } from '@features/admin/account/AccountOverview/deleteProgressStorage';
import { saveValidationProgressSession } from '@features/admin/account/AccountOverview/validationProgressStorage';
import { resourceService, fileService } from '@libs/axios';
import {
    fetchingAccountList,
    createAccount,
    getAccountDetail,
    updateAccount,
    clearAccount,
    fetchDistrictList,
    fetchProviceList,
    fetchWardList,
    fetchScDistrictList,
    fetchScProviceList,
    fetchScWardList,
    fetchRoleList,
    addStudentToClass,
    removeStudentFromClass,
    createAccountSeries,
    enrollAccountListCourses,
    unenrollAccountListCourses,
    exportAccountList,
    downloadTemplateAccountList,
    fetchHistoryLogs,
    retryHistoryTask,
    retrySessionFailures,
    deleteUser,
    bulkDeleteAccounts,
    fetchDeleteHistoryLogs,
    validateAccountSeries,
    downloadValidatedExcel,
} from '@store/actions/accountActions';
import { toast } from 'react-toastify';
import { showRetryImportFailedRequestToast } from '@utils/showRetryImportFailedRequestToast';
import { history } from '@utils/BrowserHistory';
import { convertDate } from '@utils/DateTimeUtils';
import { ResponseType } from '@store/index';

const API_URL = process.env.REACT_APP_API_URL;

function extractUserMessage(response: any) {
    if (!response?.open_edx_error) return 'Lỗi không xác định';

    const err = response.open_edx_error;
    let errorCodeMessage = null;
    let errorMessage = null;
    // Loop through all keys in open_edx_error
    for (const key of Object.keys(err)) {
        // Skip "error_code" because it's not a field
        if (key === "error_code") {
            console.log("err[key]", err[key]);
            if (err[key] === "duplicate-email-username") {
                errorCodeMessage = "Tên đăng nhập hoặc email đã tồn tại trên hệ thống nền tảng học liệu!";
            }
            else if (err[key] === "duplicate-email") {
                errorCodeMessage = "Email đã tồn tại trên hệ thống nền tảng học liệu!";
            }
            else if (err[key] === "duplicate-username") {
                errorCodeMessage = "Tên đăng nhập đã tồn tại trên hệ thống nền tảng học liệu!";
            }
            continue; // Skip the rest of the code in this loop and continue to the next iteration
        };

        const fieldErrors = err[key];

        // Should be an array of objects containing user_message
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            errorMessage = fieldErrors[0]?.user_message || null;
        }
    }

    const finalErrorMessage = errorCodeMessage == null ? errorMessage : errorCodeMessage;
    return finalErrorMessage || 'Lỗi không xác định';

}

const getUpdateErrorMessage = (errorDetail: any) => {
    let errorMessage = null;
    for (const key of Object.keys(errorDetail)) {
        if (key === "identity_number" && String(errorDetail[key]).includes("Duplicate") || key === "identity_number" && String(errorDetail[key]).includes("already exists")) {
            errorMessage = "CCCD đã tồn tại";
        }
        if (key === "email" && String(errorDetail[key]).includes("Duplicate") || key === "email" && String(errorDetail[key]).includes("already exists")) {
            errorMessage = "Email đã tồn tại";
        }
        if (key === "username" && String(errorDetail[key]).includes("Duplicate") || key === "username" && String(errorDetail[key]).includes("already exists")) {
            errorMessage = "Tên đăng nhập đã tồn tại";
        }
        if (key === "error") {
            errorMessage = String(errorDetail[key]);
        }
    }
    if (errorMessage) {
        return "Chỉnh sửa thông tin tài khoản thất bại: " + errorMessage;
    } else {
        return "Chỉnh sửa thông tin tài khoản thất bại";
    }
}

function* fetchAccountListData(action: ReturnType<typeof fetchingAccountList>) {
    const { search_term, province, district, ward, school, classes, role, is_active, is_k12, page = 1, page_size = 20, newProvince, newWard, sort_az, k12_username } = action.payload || {};
    yield put(setLoading(true));
    const params: any = {
        search_term,
        province,
        district,
        ward,
        school,
        classes,
        role,
        page,
        page_size,
        ward_2: newWard || null,
        province_2: newProvince || null,
        sort_az: sort_az || false,
    };

    if (is_active !== null && is_active !== undefined) {
        params.status = is_active ? 1 : 0;
    }

    if (is_k12 === true) {
        params.is_k12 = true;
    } else if (is_k12 === false) {
        params.is_k12 = false;
    }

    if (k12_username) {
        params.k12_username = k12_username;
    }

    try {
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + '/api/users/', {
            params
        }
        );

        if (response?.data) {
            const transformedResults = response.data.results.map((result: any) => {
                if (result.classes && result.classes.length > 0) {
                    result.school_ward = result.classes[result.classes.length - 1]?.school_ward;
                    result.school_district = result.classes[result.classes.length - 1]?.school_district;
                    result.school_province = result.classes[result.classes.length - 1]?.school_province;
                    result.school_province_name = result.classes[result.classes.length - 1]?.school_province_name;
                    result.school_district_name = result.classes[result.classes.length - 1]?.school_district_name;
                    result.school_ward_name = result.classes[result.classes.length - 1]?.school_ward_name;
                    result.school = result.classes[result.classes.length - 1]?.school_id;
                    result.school_name = result.classes[result.classes.length - 1]?.school_name;
                    result.class_name = result.classes[result.classes.length - 1]?.class_name;
                    result.classes = result.classes[result.classes.length - 1]?.id;
                } else {
                    result.school_ward = undefined;
                    result.school_district = undefined;
                    result.school_province = undefined;
                    result.school = undefined;
                    result.school_name = undefined;
                    result.school_district_name = undefined;
                    result.school_province_name = undefined;
                    result.school_ward_name = undefined;
                    result.classes = undefined;
                }
                return result;
            });
            response.data.results = transformedResults;
            yield put(setAccountList(response.data));
            yield put(setLoading(false));
        }
    } catch (e: any) {
        yield put(setLoading(false));
        let error: Error = e;
        console.log(error);
    }
}

function* createAccountSaga(action: ReturnType<typeof createAccount>) {
    yield put(setSuccess(false));
    yield put(setLoading(true));
    yield put(setLoadingCreateAccount(true));
    const payload: any = {
        ...action.payload,
        birth_date: convertDate(action.payload?.birth_date),
        ...(action.payload?.classes != null
            ? { classes: Array.isArray(action.payload.classes) ? action.payload.classes : [action.payload.classes] }
            : { classes: [] })
    };
    if (action.payload?.identity_number) {
        payload.identity_number = String(action.payload?.identity_number);
    }

    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + '/api/account/', payload);
        if (response.status == 201) {
            if (response.data?.errors && response.data?.errors.length > 0) {
                response.data.errors.forEach((enrollError: { open_edx_error: string, error_type: string }) => {
                    console.log(enrollError.open_edx_error);
                });
            }
            if (response.data.user_classes && response.data.user_classes.length > 0) {
                response.data.list_classes = response.data.user_classes;
                response.data.school_ward = response.data.user_classes[response.data.user_classes.length - 1].school_ward;
                response.data.school_district = response.data.user_classes[response.data.user_classes.length - 1].school_district;
                response.data.school_province = response.data.user_classes[response.data.user_classes.length - 1].school_province;
                response.data.school_province_name = response.data.user_classes[response.data.user_classes.length - 1].school_province_name;
                response.data.school_district_name = response.data.user_classes[response.data.user_classes.length - 1].school_district_name;
                response.data.school_ward_name = response.data.user_classes[response.data.user_classes.length - 1].school_ward_name;
                response.data.classes = response.data.user_classes[response.data.user_classes.length - 1].id;
                response.data.school = String(response.data.user_classes[response.data.user_classes.length - 1].school_id);
                response.data.school_name = response.data.user_classes[response.data.user_classes.length - 1].school_name;
                response.data.class_name = response.data.user_classes[response.data.user_classes.length - 1].class_name;
            } else {
                response.data.list_classes = undefined;
                response.data.school_ward = undefined;
                response.data.school_district = undefined;
                response.data.school_province = undefined;
                response.data.school_province_name = undefined;
                response.data.school_district_name = undefined;
                response.data.school_ward_name = undefined;
                response.data.classes = undefined;
                response.data.school = undefined;
                response.data.school_name = undefined;
                response.data.class_name = undefined;
            }
            if (response.data?.wards?.code) {
                response.data.wards_code = response.data.wards.code;
            }
            if (response.data?.wards?.district?.code) {
                response.data.district = response.data.wards.district.code;
            }
            if (response.data?.role) {
                response.data.role = response.data.role.toString();
            }
            if (response.data?.wards?.district?.province?.code) {
                response.data.province = response.data.wards.district.province.code;
            }
            response.data.role = String(response.data.role);
            response.data.ward_code_2 = response.data?.wards_2?.ward_code_2 == undefined ? undefined : String(response.data?.wards_2?.ward_code_2),
                response.data.province_code_2 = response.data?.wards_2?.province_code_2 == undefined ? undefined : String(response.data?.wards_2?.province_code_2)
            if (payload.user_existed === null) {
                // toast.success('Tạo tài khoản nền tảng học liệu mới thành công');
                toast.success('Tạo tài khoản LMS mới thành công');
            } else {
                toast.success('Chỉnh sửa thông tin tài khoản thành công');
            }
            yield put(setSuccess(true));
            yield put(setLoading(false));
            yield put(setLoadingCreateAccount(false));
            yield put(setAccountCreation(response.data));
            history.push(`/admin/account/accountdetail/?mode=view&id=${response.data.id}`);
            return true;
        }
    } catch (error: any) {
        console.log(error.response.data);
        const { email, lms_error } = error.response?.data || {}
        if (email) {
            toast.error("Email đã tồn tại!")
        }
        else if (lms_error) {
            toast.error("Tên đăng nhập đã tồn tại!")
        }
        if ('open_edx_error' in error.response.data) {
            const errorInfo = extractUserMessage(error.response.data) || "Lỗi không xác định";
            console.log("errorInfo", errorInfo);

            toast.error(`Tạo tài khoản mới thất bại: ${errorInfo}`);
            // toast.error(`Tạo tài khoản OpenEdx mới thất bại: ${errorInfo}`);
            // if (payload.user_existed === null) {
            //     toast.error('Tạo tài khoản ICDL mới thất bại');
            // } else {
            //     toast.error('Chỉnh sửa thông tin tài khoản thất bại');
            // }
            yield put(setSuccess(false));
        } else {
            if (payload.user_existed === null) {
                toast.error('Tạo tài khoản mới thất bại');
                // toast.error('Tạo tài khoản OpenEdx thất bại');
            } else {
                toast.error('Chỉnh sửa thông tin tài khoản thất bại');
            }
            yield put(setSuccess(false));
        }
        yield put(setLoading(false));
        yield put(setLoadingCreateAccount(false));
        return false;
    }
}

function* getAccountDetailSaga(action: any) {
    const { id } = action.payload;
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.get, API_URL + `/api/users/${id}/`);
        if (response?.data) {
            if (response.data.classes && response.data.classes.length > 0) {
                response.data.list_classes = response.data.classes;
                response.data.school_ward = response.data.classes[response.data.classes.length - 1].school_ward;
                response.data.school_district = response.data.classes[response.data.classes.length - 1].school_district;
                response.data.school_province = response.data.classes[response.data.classes.length - 1].school_province;
                response.data.school_province_name = response.data.classes[response.data.classes.length - 1].school_province_name;
                response.data.school_district_name = response.data.classes[response.data.classes.length - 1].school_district_name;
                response.data.school_ward_name = response.data.classes[response.data.classes.length - 1].school_ward_name;
                response.data.school = String(response.data.classes[response.data.classes.length - 1].school_id);
                response.data.school_name = response.data.classes[response.data.classes.length - 1].school_name;
                response.data.class_name = response.data.classes[response.data.classes.length - 1].class_name;
                response.data.classes = response.data.classes[response.data.classes.length - 1].id;
            } else {
                response.data.list_classes = undefined;
                response.data.school_ward = undefined;
                response.data.school_district = undefined;
                response.data.school_province = undefined;
                response.data.school_province_name = undefined;
                response.data.school_district_name = undefined;
                response.data.school_ward_name = undefined;
                response.data.school = undefined;
                response.data.school_name = undefined;
                response.data.class_name = undefined;
                response.data.classes = undefined;
            }
            yield put(
                setAccountCreation({
                    first_name: response.data?.first_name,
                    last_name: response.data?.last_name,
                    username: response.data?.username,
                    email: response.data?.email,
                    role_name: response.data?.role_name,
                    role_code: response.data?.role_code,
                    role: String(response.data?.role),
                    bank_id: response.data?.bank_id,
                    wards_code: response.data?.wards?.code,
                    district: response.data?.wards?.district?.code,
                    province: response.data?.wards?.district?.province?.code,
                    bank_account_name: response.data?.bank_account_name,
                    bank_account_number: response.data?.bank_account_number,
                    identity_number: response.data?.identity_number,
                    genre: response.data?.genre,
                    avatar_url: response.data?.avatar_url,
                    code: response.data?.code,
                    phone: response.data?.phone,
                    postal_code: response.data?.postal_code,
                    address: response.data?.address,
                    classes: response.data?.classes,
                    class_name: response.data?.class_name,
                    school: response.data?.school,
                    school_name: response.data?.school_name,
                    school_ward: response.data?.school_ward,
                    school_district: response.data?.school_district,
                    school_province: response.data?.school_province,
                    school_province_name: response.data?.school_province_name,
                    school_district_name: response.data?.school_district_name,
                    school_ward_name: response.data?.school_ward_name,
                    birth_date: response.data?.birth_date,
                    is_active: response.data?.is_active,
                    is_edx_staff: response.data?.is_edx_staff,
                    list_classes: response.data?.list_classes,
                    ward_code_2: response.data?.wards_2?.ward_code_2 == undefined ? undefined : String(response.data?.wards_2?.ward_code_2),
                    province_code_2: response.data?.wards_2?.province_code_2 == undefined ? undefined : String(response.data?.wards_2?.province_code_2),
                    openedx_id: response.data?.openedx_id,
                    k12_username: response.data?.k12_username ?? null,
                }),
            );
            yield put(setLoading(false));
        }
    } catch (error: any) {
        yield put(setLoading(false));
        console.log(error);
    }
}

function* clearAccountSaga(action: any) {
    yield put(clearAccountData());
}

function* updateAccountSaga(action: any): Generator<any, void, any> {
    yield put(setSuccess(false));
    yield put(setLoading(true));
    const { data, id } = action.payload;
    const is_by_student = data?.is_by_student;
    const payload = {
        ...data,
        birth_date: convertDate(data?.birth_date),
        ...(data?.classes != null
            ? { classes: Array.isArray(data.classes) ? data.classes : [data.classes] }
            : { classes: [] })
    };
    if (data?.identity_number) {
        payload.identity_number = String(data?.identity_number);
    }
    try {
        const response: ResponseType = yield call(resourceService.put, API_URL + `/api/account/${id}/`, payload);
        if (response.status == 200) {
            if (response.data?.errors && response.data?.errors.length > 0) {
                response.data.errors.forEach((enrollError: { open_edx_error: string, error_type: string }) => {
                    console.log(enrollError.open_edx_error);
                });
            }
            if (response.data.user_classes && response.data.user_classes.length > 0) {
                response.data.list_classes = response.data.user_classes;
                response.data.school_ward = response.data.user_classes[response.data.user_classes.length - 1].school_ward;
                response.data.school_district = response.data.user_classes[response.data.user_classes.length - 1].school_district;
                response.data.school_province = response.data.user_classes[response.data.user_classes.length - 1].school_province;
                response.data.school_province_name = response.data.user_classes[response.data.user_classes.length - 1].school_province_name;
                response.data.school_district_name = response.data.user_classes[response.data.user_classes.length - 1].school_district_name;
                response.data.school_ward_name = response.data.user_classes[response.data.user_classes.length - 1].school_ward_name;
                response.data.classes = response.data.user_classes[response.data.user_classes.length - 1].id;
                response.data.school = String(response.data.user_classes[response.data.user_classes.length - 1].school_id);
                response.data.school_name = response.data.user_classes[response.data.user_classes.length - 1].school_name;
                response.data.class_name = response.data.user_classes[response.data.user_classes.length - 1].class_name;
            } else {
                response.data.list_classes = undefined;
                response.data.school_ward = undefined;
                response.data.school_district = undefined;
                response.data.school_province = undefined;
                response.data.school_province_name = undefined;
                response.data.school_district_name = undefined;
                response.data.school_ward_name = undefined;
                response.data.classes = undefined;
                response.data.school = undefined;
                response.data.school_name = undefined;
                response.data.class_name = undefined;
            }
            toast.success('Chỉnh sửa thông tin tài khoản thành công');
            yield put(setSuccess(true));
            yield put(setLoading(false));
            yield put(
                setAccountCreation({
                    first_name: response.data?.first_name,
                    last_name: response.data?.last_name,
                    username: response.data?.username,
                    email: response.data?.email,
                    role_name: response.data?.role_name,
                    role_code: response.data?.role_code,
                    role: String(response.data?.role),
                    bank_id: response.data?.bank_id,
                    wards_code: response.data?.wards?.code,
                    district: response.data?.wards?.district?.code,
                    province: response.data?.wards?.district?.province?.code,
                    bank_account_name: response.data?.bank_account_name,
                    bank_account_number: response.data?.bank_account_number,
                    identity_number: response.data?.identity_number,
                    genre: response.data?.genre,
                    avatar_url: response.data?.avatar_url,
                    code: response.data?.code,
                    phone: response.data?.phone,
                    postal_code: response.data?.postal_code,
                    address: response.data?.address,
                    birth_date: response.data?.birth_date,
                    is_active: response.data?.is_active,
                    is_edx_staff: response.data?.is_edx_staff,
                    classes: response.data.classes,
                    school: response.data.school,
                    class_name: response.data.class_name,
                    school_name: response.data.school_name,
                    school_ward: response.data.school_ward,
                    school_district: response.data.school_district,
                    school_province: response.data.school_province,
                    school_province_name: response.data.school_province_name,
                    school_district_name: response.data.school_district_name,
                    school_ward_name: response.data.school_ward_name,
                    list_classes: response.data.list_classes,
                    ward_code_2: response.data?.wards_2?.ward_code_2 == undefined ? undefined : String(response.data?.wards_2?.ward_code_2),
                    province_code_2: response.data?.wards_2?.province_code_2 == undefined ? undefined : String(response.data?.wards_2?.province_code_2),
                    openedx_id: response.data?.openedx_id,
                    k12_username: response.data?.k12_username ?? null,
                }),
            );
            if (is_by_student) {
                history.push(`/student/profile`);
            } else {
                history.push(`/admin/account/accountdetail/?mode=view&id=${response.data.id}`);
            }
        }
    } catch (error: any) {
        // toast.error('Chỉnh sửa thông tin tài khoản thất bại');
        const detail = error.response?.data;
        console.log("detail", detail);
        toast.error(getUpdateErrorMessage(detail));
        console.log(error);
        yield put(setSuccess(false));
        yield put(setLoading(false));
    }
}

function* fetchProviceListSaga(action: any) {
    try {
        yield put(setLoadingProvince(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/provinces/?page=1&page_size=100`,
        );
        if (response.status === 200) {
            yield put(setProvinceList(response.data?.results));
            yield put(setLoadingProvince(false));
        }
    } catch (error: any) {
        yield put(setLoadingProvince(false));
        console.log(error);
    }
}

function* fetchDistricListSaga(action: any) {
    try {
        const { provinceId } = action.payload;
        // Avoid API call with absurd param
        if (provinceId === undefined || provinceId === null) {
            yield put(setDistrictList([]));
            yield put(setLoadingDistrict(true));
        } else {
            yield put(setLoadingDistrict(true));
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/districts/?province=${provinceId}&page_size=500`,
            );
            if (response.status === 200) {
                yield put(setDistrictList(response.data?.results));
                yield put(setLoadingDistrict(false));
            }
        }
    } catch (error: any) {
        yield put(setLoadingDistrict(false));
        console.log(error);
    }
}

function* fetchWardListSaga(action: any) {
    try {
        yield put(setLoadingWard(true));
        const { districtId } = action.payload;
        // Avoid API call with absurd param
        if (districtId === undefined || districtId === null) {
            yield put(setWardList([]));
            yield put(setLoadingWard(true));
        } else {
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/wards?district=${districtId}&page_size=500`,
            );
            if (response.status === 200) {
                yield put(setWardList(response.data?.results));
                yield put(setLoadingWard(false));
            }
        }
    } catch (error: any) {
        yield put(setLoadingWard(false));
        console.log(error);
    }
}

function* fetchProviceScListSaga(action: any) {
    try {
        yield put(setLoadingScProvince(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/provinces/?page=1&page_size=100`,
        );
        if (response.status === 200) {
            yield put(setProvinceScList(response.data?.results));
            yield put(setLoadingScProvince(false));
        }
    } catch (error: any) {
        yield put(setLoadingScProvince(false));
        console.log(error);
    }
}

function* fetchDistricScListSaga(action: any) {
    try {
        const { provinceId } = action.payload;
        // Avoid API call with absurd param
        if (provinceId === undefined || provinceId === null) {
            yield put(setDistrictScList([]));
            yield put(setLoadingScDistrict(true));
        } else {
            yield put(setLoadingScDistrict(true));
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/districts/?province=${provinceId}&page_size=500`,
            );
            if (response.status === 200) {
                yield put(setDistrictScList(response.data?.results));
                yield put(setLoadingScDistrict(false));
            }
        }
    } catch (error: any) {
        yield put(setLoadingScDistrict(false));
        console.log(error);
    }
}

function* fetchWardScListSaga(action: any) {
    try {
        yield put(setLoadingScWard(true));
        const { districtId } = action.payload;
        // Avoid API call with absurd param
        if (districtId === undefined || districtId === null) {
            yield put(setWardScList([]));
            yield put(setLoadingScWard(true));
        } else {
            const response: ResponseType = yield call(
                resourceService.get,
                API_URL + `/api/wards?district=${districtId}&page_size=500`,
            );
            if (response.status === 200) {
                yield put(setWardScList(response.data?.results));
                yield put(setLoadingScWard(false));
            }
        }
    } catch (error: any) {
        yield put(setLoadingScWard(false));
        console.log(error);
    }
}

function* fetchRoleListSaga(action: any) {
    try {
        yield put(setLoadingRole(true));
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + `/api/roles/?page=1&page_size=40&status=1`,
        );
        if (response.status === 200) {
            yield put(setRoleList(response.data?.results));
            yield put(setLoadingRole(false));
        }
    } catch (error: any) {
        yield put(setLoadingRole(false));
        console.error(error);
    }
}

function* addStudentToClassSaga(action: ReturnType<typeof addStudentToClass>) {
    yield put(setLoading(true));
    const { classId, userIds } = action.payload;
    const url = `${API_URL}/api/classes/${classId}/add_students/`;
    const payload = { user_id: userIds };

    try {
        const response: ResponseType = yield call(resourceService.post, url, payload);
        if (response.status === 200) {
            toast.success('Thêm học sinh vào lớp thành công');
            // Update local state or fetch updated class data if needed
        }
    } catch (error: any) {
        toast.error('Thêm học sinh vào lớp thất bại');
        console.log(error);
    } finally {
        yield put(setLoading(false));
    }
}

function* removeStudentFromClassSaga(action: ReturnType<typeof removeStudentFromClass>) {
    yield put(setLoading(true));
    const { classId, userIds } = action.payload;
    const url = `${API_URL}/api/classes/${classId}/remove_students/`;
    const payload = { user_id: userIds };

    try {
        const response: ResponseType = yield call(resourceService.delete, url, { data: payload });
        if (response.status === 200) {
            toast.success('Xóa học sinh khỏi lớp thành công');
            // Update local state or fetch updated class data if needed
        }
    } catch (error: any) {
        toast.error('Xóa học sinh khỏi lớp thất bại');
        console.log(error);
    } finally {
        yield put(setLoading(false));
    }
}

function* createAccountSeriesSaga(action: ReturnType<typeof createAccountSeries>) {
    // Show loading indicator only during API call
    yield put(setLoading(true));

    const accounts = action.payload;
    try {
        // Format the data for the API - backend expects numbered keys with is_series flag
        const formattedData: any = { is_series: true };
        accounts.forEach((account, index) => {
            formattedData[index] = account;
        });

        const response: ResponseType = yield call(resourceService.post, API_URL + '/api/account/', formattedData);

        // We now expect a 202 status code (Accepted) which means the tasks are queued
        if (response.status === 202) {
            // Stop loading since we only need to wait for queue confirmation
            yield put(setLoading(false));

            // Show success message that tasks are queued
            toast.success('Yêu cầu tạo tài khoản hàng loạt đã được gửi thành công và đang được xử lý');

            // Show info message that process is continuing in background
            toast.info('Bạn có thể tiếp tục sử dụng các chức năng của hệ thống trừ tạo/cập nhật tài khoản hoặc đăng kí môn học cho đến khi quá trình kết thúc');
            yield put(setFailedCreateAccount(response.data?.failed_accounts))

            const sessionId: string | undefined = response.data?.session_id;
            const totalTasks: number | undefined = response.data?.total_tasks;
            if (sessionId && totalTasks && totalTasks > 0) {
                const username: string | null = yield select((state: any) => state.auth?.username ?? null);
                const session = {
                    sessionId,
                    totalTasks,
                    createdAt: Date.now(),
                    ownerUsername: username,
                };
                saveImportProgressSession(session);
                yield put(setImportProgressSession(session));
            }

            return true;
        }

        // Handle old response format for backward compatibility
        yield put(setLoading(false));
        toast.success('Tạo thông tin tài khoản hàng loạt thành công');
        return true;
    } catch (error: any) {
        // Handle errors
        let errorMsg = 'Tạo thông tin tài khoản hàng loạt thất bại';

        if (error.response && error.response.data && error.response.data.error) {
            errorMsg += `: ${error.response.data.error}`;
        }

        toast.error(errorMsg);
        console.error(error);
        yield put(setLoading(false));
        return false;
    }
}

function* validateAccountSeriesSaga(action: ReturnType<typeof validateAccountSeries>) {
    // Dry-run preview validation — mirrors createAccountSeriesSaga's request
    // shape exactly, but posts to validate_import/ (never creates an account)
    // and tracks progress under its own session (validationSession), separate
    // from a real import's importProgressSession.
    const accounts = action.payload;
    try {
        const formattedData: any = { is_series: true };
        accounts.forEach((account, index) => {
            formattedData[index] = account;
        });

        const response: ResponseType = yield call(
            resourceService.post, API_URL + '/api/account/validate_import/', formattedData,
        );

        if (response.status === 202) {
            yield put(setValidationPreFilterFailures(response.data?.failed_validations || []));

            const sessionId: string | undefined = response.data?.session_id;
            const totalTasks: number | undefined = response.data?.total_tasks;
            if (sessionId) {
                const username: string | null = yield select((state: any) => state.auth?.username ?? null);
                const session = {
                    sessionId,
                    totalTasks: totalTasks || 0,
                    createdAt: Date.now(),
                    ownerUsername: username,
                };
                saveValidationProgressSession(session);
                yield put(setValidationSession(session));
            }
        } else {
            toast.error('Kiểm tra dữ liệu import thất bại');
        }
    } catch (error: any) {
        console.error(error);
        toast.error('Kiểm tra dữ liệu import thất bại');
    }
}

function* downloadValidatedExcelSaga(action: ReturnType<typeof downloadValidatedExcel>) {
    const { sessionId, feErrors } = action.payload;
    try {
        // Backend streams the file directly (same approach as the account-import
        // template download) instead of uploading it to S3 first — one less
        // network round-trip that could fail. The original uploaded file still
        // comes from S3 server-side (that's where it already lives), only the
        // newly-annotated OUTPUT file skips the extra upload step.
        // POST (not GET) so feErrors — which can be sizeable for a large batch —
        // goes in the body instead of a query string.
        const response: ResponseType = yield call(
            fileService.post, API_URL + '/api/account/download_validated_excel/',
            { session_id: sessionId, fe_errors: feErrors || {} },
            { responseType: 'blob' },
        );
        if (response.status === 200) {
            yield put(setDownloadValidatedExcelFile(response.data));
        } else {
            toast.error('Tải file lỗi thất bại');
        }
    } catch (error: any) {
        console.error(error);
        toast.error('Tải file lỗi thất bại: ' + (error.response?.data?.error || 'Lỗi không xác định'));
    }
}

function* enrollAccountListCoursesSaga(action: ReturnType<typeof enrollAccountListCourses>) {
    yield put(setLoadingEnrollments(true));
    yield put(setEnrolledList([]));
    const { email, course_ids } = action.payload;
    const payload = {
        email: email,
        course_ids: course_ids
    };
    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + `/api/enroll_courses_endpoint/`, payload);
        if (response.status == 200) {
            toast.success(`Enroll thành công khóa học: ${response.data?.list_success}`);
            yield put(setLoadingEnrollments(false));
            yield put(setEnrolledList(response.data?.list_success));
        } else {
            const errorMessage = response.data?.message ?? 'Unidentified';
            const errorType = response.data?.error_type ?? 'Unidentified';
            if (errorType === 'enroll') {
                const listFailure: any[] = response.data?.list_failure ?? [];
                const listSuccess: any[] = response.data?.list_success ?? [];
                if (listSuccess.length > 0) {
                    toast.success(`Enroll thành công cho các khóa học sau: ${listSuccess}`);
                }
                if (listFailure.length > 0) {
                    toast.error(`Enroll không thành công cho các khóa học sau: ${listFailure}`);
                }
            }
            toast.error(errorMessage);
            yield put(setEnrolledList(response.data?.list_success));
            yield put(setLoadingEnrollments(false));
        }
    } catch (error: any) {
        toast.error(`Enroll course thất bại!`);
        console.log(error);
        yield put(setEnrolledList([]));
        yield put(setLoadingEnrollments(false));
    }
}

function* unenrollAccountListCoursesSaga(action: ReturnType<typeof unenrollAccountListCourses>) {
    yield put(setLoadingEnrollments(true));
    setUnEnrolledList([]);
    const { email, course_ids } = action.payload;
    const payload = {
        email: email,
        course_ids: course_ids
    };

    try {
        const response: ResponseType = yield call(resourceService.post, API_URL + `/api/unenroll_courses_endpoint/`, payload);
        if (response.status == 200) {
            toast.success(`Unenroll thành công khóa học: ${response.data?.list_success}`);
            yield put(setLoadingEnrollments(false));
            yield put(
                setUnEnrolledList(response.data?.list_success),
            );
        } else {
            const errorMessage = response.data?.message ?? 'Unidentified';
            const errorType = response.data?.error_type ?? 'Unidentified';
            if (errorType === 'enroll') {
                const listFailure: any[] = response.data?.list_failure ?? [];
                const listSuccess: any[] = response.data?.list_success ?? [];
                if (listSuccess.length > 0) {
                    toast.success(`Unenroll thành công cho các khóa học sau: ${listSuccess}`);
                }
                if (listFailure.length > 0) {
                    toast.error(`Unenroll không thành công cho các khóa học sau: ${listFailure}`);
                }
            }
            toast.error(errorMessage);
            setUnEnrolledList(response.data?.list_success)
            yield put(setLoadingEnrollments(false));
        }
    } catch (error: any) {
        toast.error(`Enroll course thất bại!`);
        console.log(error);
        setUnEnrolledList([])
        yield put(setLoadingEnrollments(false));
    }
}

function* exportAccountListSaga(action: ReturnType<typeof exportAccountList>) {
    yield put(setLoadingExport(true));
    const { search_term, province, district, ward, school, classes, role, is_active, is_k12, page = 1, page_size = 20, is_export } = action.payload || {};

    const params: any = {
        search_term,
        province,
        district,
        ward,
        school,
        classes,
        role,
        page,
        page_size,
        is_export: true // Mandatory for export
    };

    if (is_active !== null && is_active !== undefined) {
        params.status = is_active ? 1 : 0;
    }

    if (is_k12 === true) {
        params.is_k12 = true;
    } else if (is_k12 === false) {
        params.is_k12 = false;
    }

    try {
        // Use resourceService for JSON response
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + '/api/users/', {
            params: params
        }
        );

        if (response.status == 200) {
            // Handle JSON response with file_url
            const responseData = response.data;
            if (responseData && responseData.file_url) {
                yield put(setExportFileUrl(responseData.file_url));
            } else {
                toast.error('Xuất file thất bại: Không nhận được URL file từ server');
            }
        } else {
            toast.error('Xuất file thất bại: ' + response.data.error);
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Xuất file thất bại: ' + (error.response?.data?.error || error.message));
    } finally {
        yield put(setLoadingExport(false));
    }
}

function* downloadTemplateAccountListSaga(action: ReturnType<typeof downloadTemplateAccountList>) {
    yield put(setLoadingDownloadTemplate(true));
    try {
        const response: ResponseType = yield call(
            fileService.get,
            API_URL + '/api/download-static-excel/',
            {
                responseType: 'blob' // Set response type to blob for binary data
            }
        );
        if (response.status === 200) {
            yield put(setTemplateFile(response.data));
        } else {
            toast.error('Tải file template thất bại');
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Tải file template thất bại: ' + (error.response?.data?.error || 'Lỗi không xác định'));
    } finally {
        yield put(setLoadingDownloadTemplate(false));
    }
}

function* fetchHistoryLogsSaga(action: ReturnType<typeof fetchHistoryLogs>) {
    yield put(setLoadingHistoryLogs(true));
    const { page, page_size, create_account_status, username } = action.payload;
    const params = {
        page,
        page_size,
        create_account_status,
        username
    }

    try {
        const response: ResponseType = yield call(resourceService.get, API_URL + '/api/task_history/?task_type=create_single_account', { params: params });
        if (response.status === 200) {
            yield put(setHistoryLogs(response.data));
        } else {
            toast.error('Lấy lịch sử thao tác thất bại: ' + response.data.error);
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Lấy lịch sử thao tác thất bại: ' + error.response.data.error);
    } finally {
        yield put(setLoadingHistoryLogs(false));
    }
}

function* fetchDeleteHistoryLogsSaga(action: ReturnType<typeof fetchDeleteHistoryLogs>) {
    yield put(setLoadingDeleteHistoryLogs(true));
    const { page, page_size, status, username } = action.payload;
    const params = {
        page,
        page_size,
        status,
        username,
    };

    try {
        const response: ResponseType = yield call(
            resourceService.get,
            API_URL + '/api/task_history/?task_type=delete_single_account',
            { params },
        );
        if (response.status === 200) {
            yield put(setDeleteHistoryLogs(response.data));
        } else {
            toast.error('Lấy lịch sử xóa tài khoản thất bại: ' + response.data?.error);
        }
    } catch (error: any) {
        console.log(error);
        toast.error('Lấy lịch sử xóa tài khoản thất bại: ' + (error?.response?.data?.error || ''));
    } finally {
        yield put(setLoadingDeleteHistoryLogs(false));
    }
}

function* retryHistoryTaskSaga(action: ReturnType<typeof retryHistoryTask>) {
    const { taskId } = action.payload;
    try {
        const response: ResponseType = yield call(resourceService.post, `${API_URL}/api/task_history/${taskId}/retry/`);
        if (response.status === 202) {
            toast.success('Đã gửi yêu cầu chạy lại tác vụ');
            // Optionally refresh logs
        } else {
            toast.error(response.data?.detail || 'Không thể chạy lại tác vụ');
        }
    } catch (error: any) {
        toast.error(error?.response?.data?.detail || 'Không thể chạy lại tác vụ');
    } finally {
    }
}

function* retrySessionFailuresSaga(action: ReturnType<typeof retrySessionFailures>) {
    const { sessionId } = action.payload;
    try {
        const response: ResponseType = yield call(
            resourceService.post,
            `${API_URL}/api/task_history/retry_session/`,
            { session_id: sessionId },
        );
        if (response.status === 202 || response.status === 200) {
            showRetryImportFailedRequestToast();
        } else {
            toast.error(response.data?.detail || 'Không thể chạy lại các tác vụ thất bại');
        }
    } catch (error: any) {
        toast.error(error?.response?.data?.detail || 'Không thể chạy lại các tác vụ thất bại');
    }
}

function* deleteUserSaga(
    action: ReturnType<typeof deleteUser>,
): Generator<any, void, any> {
    const { id } = action.payload;
    yield put(setLoadingDeleteUser(true));
    try {
        const response: ResponseType = yield call(
            resourceService.delete,
            API_URL + `/api/users/${id}/`,
        );
        if (response.status === 200 || response.status === 204) {
            const successDetail =
                typeof response.data?.detail === 'string' && response.data.detail.trim()
                    ? response.data.detail
                    : 'Đã xóa tài khoản thành công.';
            toast.success(successDetail);
            yield put(setDeleteUserSuccess(true));
        }
    } catch (error: any) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const detail = data?.detail;
        if (status === 400) {
            toast.error(
                typeof detail === 'string' && detail.trim()
                    ? detail
                    : 'Yêu cầu không hợp lệ.',
            );
        } else if (status === 502) {
            toast.error(
                typeof detail === 'string' && detail.trim()
                    ? detail
                    : 'Xóa tài khoản trên nền tảng học liệu thất bại. Tài khoản LMS đã được khôi phục.',
            );
        } else if (status === 403) {
            toast.error('Bạn không có quyền thực hiện thao tác này.');
        } else if (status !== 401) {
            const msg =
                detail ||
                data?.message ||
                error?.message ||
                'Xóa tài khoản thất bại';
            toast.error(typeof msg === 'string' ? msg : 'Xóa tài khoản thất bại');
        }
        console.error(error);
    } finally {
        yield put(setLoadingDeleteUser(false));
    }
}

function* bulkDeleteAccountsSaga(
    action: ReturnType<typeof bulkDeleteAccounts>,
): Generator<any, void, any> {
    const { emails } = action.payload;
    try {
        const response: ResponseType = yield call(
            resourceService.post,
            API_URL + '/api/users/bulk_delete_async/',
            { emails },
        );

        if (response.status === 202) {
            toast.success('Yêu cầu xóa tài khoản hàng loạt đã được gửi thành công và đang được xử lý');
            toast.info('Bạn có thể tiếp tục sử dụng các chức năng của hệ thống cho đến khi quá trình kết thúc');

            const sessionId: string | undefined = response.data?.session_id;
            const totalTasks: number | undefined = response.data?.total_tasks;
            if (sessionId && totalTasks && totalTasks > 0) {
                const username: string | null = yield select((state: any) => state.auth?.username ?? null);
                const session = {
                    sessionId,
                    totalTasks,
                    createdAt: Date.now(),
                    ownerUsername: username,
                };
                saveDeleteProgressSession(session);
                yield put(setDeleteProgressSession(session));
            }
        }
    } catch (error: any) {
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Xóa tài khoản hàng loạt thất bại');
        console.error(error);
    }
}

function* accountSaga() {
    yield takeLatest(fetchingAccountList, fetchAccountListData),
        yield takeLatest(createAccount, createAccountSaga),
        yield takeLatest(getAccountDetail, getAccountDetailSaga),
        yield takeLatest(clearAccount, clearAccountSaga),
        yield takeLatest(updateAccount as any, updateAccountSaga),
        yield takeLatest(fetchProviceList, fetchProviceListSaga),
        yield takeLatest(fetchDistrictList, fetchDistricListSaga),
        yield takeLatest(fetchWardList, fetchWardListSaga),
        yield takeLatest(fetchRoleList, fetchRoleListSaga),
        yield takeLatest(fetchScProviceList, fetchProviceScListSaga),
        yield takeLatest(fetchScDistrictList, fetchDistricScListSaga),
        yield takeLatest(fetchScWardList, fetchWardScListSaga),
        yield takeLatest(addStudentToClass, addStudentToClassSaga);
    yield takeLatest(removeStudentFromClass, removeStudentFromClassSaga);
    yield takeLatest(createAccountSeries, createAccountSeriesSaga);
    yield takeLatest(enrollAccountListCourses, enrollAccountListCoursesSaga);
    yield takeLatest(unenrollAccountListCourses, unenrollAccountListCoursesSaga);
    yield takeLatest(exportAccountList, exportAccountListSaga);
    yield takeLatest(downloadTemplateAccountList, downloadTemplateAccountListSaga);
    yield takeLatest(fetchHistoryLogs, fetchHistoryLogsSaga);
    yield takeLatest(fetchDeleteHistoryLogs, fetchDeleteHistoryLogsSaga);
    yield takeLatest(retryHistoryTask, retryHistoryTaskSaga);
    yield takeLatest(retrySessionFailures, retrySessionFailuresSaga);
    yield takeLatest(deleteUser.type, deleteUserSaga);
    yield takeLatest(bulkDeleteAccounts, bulkDeleteAccountsSaga);
    yield takeLatest(validateAccountSeries, validateAccountSeriesSaga);
    yield takeLatest(downloadValidatedExcel, downloadValidatedExcelSaga);
}

export default accountSaga;
